/**
 * Vista previa de una plantilla con datos de ejemplo o con un documento REAL existente
 * (no crea ni modifica nada; no registra uso de la plantilla).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { EyeIcon, XIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

export async function openPdfBlob(promise) {
  const resp = await promise;
  const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
  window.open(url, "_blank");
}

export default function PreviewRealDocModal({ template, layout = null, onClose }) {
  const [q, setQ] = useState("");
  const [docs, setDocs] = useState([]);
  const [docId, setDocId] = useState("");
  const [busy, setBusy] = useState(false);
  const tipo = template?.tipo;

  useEffect(() => {
    if (!tipo) return undefined;
    const h = setTimeout(() => {
      api.get(`/document-templates/sample-docs/${tipo}`, { params: q ? { q } : {} })
        .then(({ data }) => setDocs(data || []))
        .catch(() => setDocs([]));
    }, 250);
    return () => clearTimeout(h);
  }, [tipo, q]);

  const run = async (real) => {
    setBusy(true);
    try {
      if (real && !docId) { toast.error("Selecciona un documento"); return; }
      if (layout) {
        // Editor en vivo: layout sin guardar
        await openPdfBlob(api.post("/document-templates/preview-layout", layout,
          { responseType: "blob", params: real ? { tipo, doc_id: docId } : { tipo } }));
      } else if (real) {
        await openPdfBlob(api.post(`/document-templates/${template.id}/preview-with/${tipo}/${docId}`, {},
          { responseType: "blob" }));
      } else {
        await openPdfBlob(api.post(`/document-templates/${template.id}/preview`, {}, { responseType: "blob" }));
      }
    } catch (e) {
      toast.error("No se pudo generar la vista previa");
    } finally {
      setBusy(false);
    }
  };

  const vm = useModalForm({ onClose: onClose, abierto: Boolean(template) });
  if (!template) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="preview-modal" {...vm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg">Vista previa — {template.nombre}</h3>
          <button aria-label="Cerrar" onClick={vm.cerrar} className="text-text-muted hover:text-white"><XIcon size={18} /></button>
        </div>
        <button onClick={() => run(false)} disabled={busy} data-testid="preview-sample"
          className="w-full mb-4 border border-border rounded-md px-3 py-2 text-sm hover:border-agro-green flex items-center gap-2 justify-center">
          <EyeIcon size={16} /> Con datos de ejemplo
        </button>
        <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Con un documento real existente</div>
        <div className="flex items-center gap-2 mb-2">
          <MagnifyingGlassIcon size={14} className="text-text-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por número o nombre"
            className="flex-1 bg-background border border-border rounded px-2 py-1.5 text-sm" data-testid="preview-search" />
        </div>
        <select value={docId} onChange={(e) => setDocId(e.target.value)} data-testid="preview-doc-select"
          className="w-full bg-background border border-border rounded px-2 py-1.5 text-sm mb-3">
          <option value="">— Selecciona —</option>
          {docs.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
        </select>
        <button onClick={() => run(true)} disabled={busy || !docId} data-testid="preview-real"
          className="w-full bg-agro-green text-black font-semibold rounded-md px-3 py-2 text-sm disabled:opacity-50">
          {busy ? "Generando…" : "Ver con documento real"}
        </button>
        <p className="text-[11px] text-text-muted mt-3">La vista previa no crea ni modifica documentos.</p>
      </div>
    </div>
  );
}
